import { Table } from "@tanstack/react-table";
import isNil from "lodash/isNil";
import isObject from "lodash/isObject";
import { RefObject, useCallback, useRef } from "react";

import { normalizedRange } from "./cell_selection_utils";
import { InteractiveTableStoreApi } from "./interactive_table.store";
import { CellRange, CopyEvent, PasteEvent, TableBounds } from "./interactive_table.types";

type UseCellCopyPasteProps = {
  store: InteractiveTableStoreApi;
  tableInstanceRef?: RefObject<Table<any> | null>;
  onCopy?: (event: CopyEvent) => void;
  onPaste?: (event: PasteEvent) => void;
  restrictPasteToSourceColumn?: boolean;
};

const primitiveToString = (value: unknown): string => {
  if (isNil(value) || isObject(value)) {
    return "";
  }
  return String(value);
};

export const useCellCopyPaste = ({
  store,
  tableInstanceRef,
  onCopy,
  onPaste,
  restrictPasteToSourceColumn
}: UseCellCopyPasteProps) => {
  const copiedColRef = useRef<number | undefined>(undefined);
  const copySelection = useCallback(async () => {
    const { selectionRange } = store.getState();
    const tableInstance = tableInstanceRef?.current ?? undefined;
    if (!selectionRange || !tableInstance) {
      return;
    }

    const range = normalizedRange(selectionRange);
    if (restrictPasteToSourceColumn) {
      copiedColRef.current = range.minCol;
    }
    const { rows } = tableInstance.getRowModel();

    const lines: string[] = [];
    let anyCellCopied = false;

    for (let rowIdx = range.minRow; rowIdx <= range.maxRow; rowIdx++) {
      const row = rows[rowIdx];
      if (!row) {
        continue;
      }

      const cells = row.getVisibleCells();
      const values: string[] = [];

      for (let colIdx = range.minCol; colIdx <= range.maxCol; colIdx++) {
        const cell = cells[colIdx];
        const isCopyPasteBlocked = cell?.column.columnDef.meta?.getIsCopyPasteBlocked?.(cell.row.original);

        if (!cell || isCopyPasteBlocked) {
          values.push("");
          continue;
        }

        anyCellCopied = true;
        const getCopyValue = cell.column.columnDef.meta?.getCopyValue;
        const rawValue = cell.getValue();
        values.push(getCopyValue ? getCopyValue(rawValue) : primitiveToString(rawValue));
      }

      lines.push(values.join("\t"));
    }

    const tsv = lines.join("\n");

    try {
      await navigator.clipboard.writeText(tsv);
      if (anyCellCopied) {
        store.getState().actions.setCopied(selectionRange);
      }
    } catch {
      // Clipboard write can fail in non-secure contexts — fail silently
    }

    onCopy?.({ range, tsv });
  }, [store, tableInstanceRef, onCopy, restrictPasteToSourceColumn]);

  const pasteFromAnchor = useCallback(
    async (bounds: TableBounds) => {
      const { selectionRange } = store.getState();
      if (!selectionRange || !onPaste) {
        return;
      }

      let tsv: string;
      try {
        tsv = await navigator.clipboard.readText();
      } catch {
        // Clipboard read can fail without user permission — fail silently
        return;
      }

      if (!tsv) {
        return;
      }

      // Parse TSV into a 2D array: rows split by newline, cells split by tab.
      // Trim a single trailing newline that some applications append.
      const values: string[][] = tsv
        .replace(/\n$/, "")
        .split("\n")
        .map((line) => line.split("\t"));

      const { anchor } = selectionRange;

      if (
        restrictPasteToSourceColumn &&
        copiedColRef.current !== undefined &&
        anchor.colIndex !== copiedColRef.current
      ) {
        return;
      }

      onPaste({ anchor, values, tsv });

      // Compute the actually-pasted area, clamped to the table bounds so that
      // pasting near the bottom-right edge selects only the cells that exist.
      const pasteRowCount = values.length;
      const pasteColCount = Math.max(...values.map((row) => row.length));

      const pastedRange: CellRange = {
        anchor,
        active: {
          rowIndex: Math.min(anchor.rowIndex + pasteRowCount - 1, bounds.rowCount - 1),
          colIndex: Math.min(anchor.colIndex + pasteColCount - 1, bounds.colCount - 1)
        }
      };

      store.getState().actions.setSelection(pastedRange);
    },
    [store, onPaste, restrictPasteToSourceColumn]
  );

  return { copySelection, pasteFromAnchor };
};
